import React, { useState } from "react";
import { Box, Flex, Image, Text } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { Loading } from "../../components/ui";
import { AppBar } from "../../components/mobileUi";
import { PropCell, StatusPill, PRIMARY, BORDER, MUTED, TEXT } from "../../components/requestUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  DetailItem,
  DetailSection,
  useIsDesktop,
} from "../../components/desktopUi";
import { getRegisterOutsideByIdApi } from "../../api/requests.api";
import { resolveImageUrl } from "../../utils/format";
import { EMP_API_BASE_URL, EMP_BASE_PATH } from "../../config";

const Tab = ({ active, children, onClick }) => (
  <Flex
    as="button"
    flex="1"
    direction="column"
    align="center"
    onClick={onClick}
    pb="10px"
    borderBottom="2px solid"
    borderColor={active ? PRIMARY : "transparent"}
  >
    <Text fontSize="15px" fontWeight={600} color={active ? PRIMARY : MUTED}>
      {children}
    </Text>
  </Flex>
);

const OutsideDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [tab, setTab] = useState("overview");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["emp", "outside-detail", id],
    queryFn: () => getRegisterOutsideByIdApi(id),
  });

  const images = data?.confirmImage || [];

  // ===================== Bản WEB (desktop) =====================
  // Desktop rộng nên hiển thị cả tổng quan và ảnh xác nhận, bỏ tab.
  if (isDesktop) {
    return (
      <DesktopPage
        title="Chi tiết đơn đăng ký chấm công ngoài"
        right={
          <DesktopBack onClick={() => navigate(`${EMP_BASE_PATH}/cham-cong-ngoai`)} />
        }
      >
        {isLoading ? (
          <Loading />
        ) : isError || !data ? (
          <Text fontSize="14px" color={MUTED}>
            Không tải được dữ liệu.
          </Text>
        ) : (
          <>
            <DetailSection
              title={`Đơn ngày ${
                data.createdAt ? moment(data.createdAt).format("DD/MM/YYYY") : "--/--/----"
              }`}
              right={<StatusPill status={data.status} />}
            >
              <DetailItem label="Địa điểm" value={data.checkinPlace} />
              <DetailItem label="Số công" value={data.workingDayRate ?? 0} />
              <DetailItem label="Lý do" value={data.comment} />
            </DetailSection>

            <DesktopPanel>
              <Text fontSize="16px" fontWeight={600} color={TEXT} mb="16px">
                Ảnh xác nhận
              </Text>
              {images.length ? (
                <Flex gap="16px" flexWrap="wrap">
                  {images.map((src, i) => (
                    <Image
                      key={i}
                      src={resolveImageUrl(src, EMP_API_BASE_URL)}
                      alt=""
                      w="280px"
                      h="200px"
                      objectFit="cover"
                      borderRadius="12px"
                      border="1px solid"
                      borderColor={BORDER}
                    />
                  ))}
                </Flex>
              ) : (
                <Text fontSize="14px" color={MUTED}>
                  Chưa có ảnh xác nhận
                </Text>
              )}
            </DesktopPanel>
          </>
        )}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto">
      <AppBar title="Chi tiết đơn đăng ký chấm công ngoài" onBack={() => navigate(`${EMP_BASE_PATH}/cham-cong-ngoai`)} />

      <Box px="16px" pt="16px">
      {isLoading ? (
        <Loading />
      ) : isError || !data ? (
        <Text fontSize="14px" color={MUTED}>Không tải được dữ liệu.</Text>
      ) : (
        <Box bg="white" borderRadius="12px" border="1px solid" borderColor={BORDER} overflow="hidden">
          <Flex borderBottom="1px solid" borderColor={BORDER} pt="12px" px="0">
            <Tab active={tab === "overview"} onClick={() => setTab("overview")}>Tổng quan</Tab>
            <Tab active={tab === "images"} onClick={() => setTab("images")}>Ảnh xác nhận</Tab>
          </Flex>

          {tab === "overview" ? (
            <Box px="16px" pt="16px" pb="8px">
              <Flex align="center" gap="8px" flexWrap="wrap">
                <Text fontSize="14px" fontWeight={600} color={TEXT}>
                  {data.createdAt ? moment(data.createdAt).format("DD/MM/YYYY") : "--/--/----"}
                </Text>
                <Text fontSize="14px" color={TEXT}>-</Text>
                <StatusPill status={data.status} />
              </Flex>
              <PropCell label="Địa điểm" value={data.checkinPlace} />
              <PropCell label="Số công" value={data.workingDayRate ?? 0} />
              <PropCell label="Lý do" value={data.comment} borderBottom={false} />
            </Box>
          ) : (
            <Box px="16px" pt="16px" pb="20px">
              <Text fontSize="14px" fontWeight={600} color={TEXT} mb="16px">Ảnh xác nhận</Text>
              {images.length ? (
                <Flex direction="column" gap="16px">
                  {images.map((src, i) => (
                    <Image
                      key={i}
                      src={resolveImageUrl(src, EMP_API_BASE_URL)}
                      alt=""
                      w="100%"
                      h="240px"
                      objectFit="cover"
                      borderRadius="12px"
                      border="1px solid"
                      borderColor={BORDER}
                    />
                  ))}
                </Flex>
              ) : (
                <Text fontSize="14px" color={MUTED}>Chưa có ảnh xác nhận</Text>
              )}
            </Box>
          )}
        </Box>
      )}
      </Box>
    </Box>
  );
};

export default OutsideDetail;
